import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardBody, Form, FormGroup, Label, Input, Button, Row, Col, InputGroup, InputGroupText } from "reactstrap";
import { toast } from "react-toastify";
import { FaMoneyBillWave, FaArrowLeft, FaSave } from "react-icons/fa";
import axios from "axios";
import { useSelector } from "react-redux";

const CreateSettlement = () => {
  const navigate = useNavigate();
  const { AuthReducer: { auth } } = useSelector((state) => state);
  
  const [formData, setFormData] = useState({
    partner_id: "",
    partner_name: "",
    partner_type: "individual",
    title: "",
    description: "",
    period_start: "",
    period_end: "",
    total_revenue: "",
    commission_percent: "",
    commission_amount: "",
    tax_amount: "",
    amount_due: "",
    net_payable: "",
    status: "pending",
    payment_mode: "",
    transaction_id: "",
    payout_date: "",
    remarks: ""
  });

  const [loading, setLoading] = useState(false);
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const response = await axios.get("/api/admin/v1/partners", {
        headers: { Authorization: `Bearer ${auth?.token}` }
      });
      setPartners(response.data.PartnerLists || response.data.partners || []);
    } catch (error) {
      console.error("Failed to fetch partners");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Auto-calculate amounts
    if (name === 'total_revenue' || name === 'commission_percent') {
      const revenue = parseFloat(name === 'total_revenue' ? value : formData.total_revenue) || 0;
      const percent = parseFloat(name === 'commission_percent' ? value : formData.commission_percent) || 0;
      const commission = (revenue * percent) / 100;
      const tax = commission * 0.18; // 18% GST
      const amountDue = commission + tax;
      const netPayable = amountDue;
      
      setFormData(prev => ({
        ...prev,
        [name]: value,
        commission_amount: commission.toFixed(2),
        tax_amount: tax.toFixed(2),
        amount_due: amountDue.toFixed(2),
        net_payable: netPayable.toFixed(2)
      }));
    }
  };

  const handlePartnerSelect = (e) => {
    const partnerId = e.target.value;
    const partner = partners.find(p => p.id === parseInt(partnerId));
    
    setFormData(prev => ({
      ...prev,
      partner_id: partnerId,
      partner_name: partner ? partner.fullName || partner.businessName || partner.name : "",
      partner_type: partner ? partner.partnerType || "individual" : "individual"
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post("/api/admin/v1/settlements", formData, {
        headers: { Authorization: `Bearer ${auth?.token}` }
      });
      
      toast.success("Settlement created successfully");
      navigate("/settlements");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create settlement");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="row my-3">
        <div className="col-md-12">
          <div className="mb-4 d-flex align-items-center">
            <Button 
              color="link" 
              className="text-white p-0 me-3"
              onClick={() => navigate("/settlements")}
            >
              <FaArrowLeft size={20} />
            </Button>
            <h5 className="mb-0 text-white fs-4">
              <FaMoneyBillWave className="me-2" />
              Create Settlement
            </h5>
          </div>
        </div>
      </div>

      <Card>
        <CardBody>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={12}>
                <FormGroup>
                  <Label>Settlement Title *</Label>
                  <Input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Enter settlement title"
                    required
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <FormGroup>
                  <Label>Description</Label>
                  {/* textarea */}
                  <Input
                    type="textarea"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={3}
                    placeholder="Enter settlement description"
                  />
                 
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Partner *</Label>
                  <Input
                    type="select"
                    name="partner_id"
                    value={formData.partner_id}
                    onChange={handlePartnerSelect}
                    required
                  >
                    <option value="">Select Partner</option>
                    {partners.map(partner => (
                      <option key={partner.id} value={partner.id}>
                        {partner.fullName || partner.name}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label>Partner Type</Label>
                  <Input
                    type="select"
                    name="partner_type"
                    value={formData.partner_type}
                    onChange={handleInputChange}
                  >
                    <option value="individual">Individual</option>
                    <option value="business">Business</option>
                    <option value="government">Government</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label>Status</Label>
                  <Input
                    type="select"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                  >
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="completed">Completed</option>
                    <option value="failed">Failed</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Period Start *</Label>
                  <Input
                    type="date"
                    name="period_start"
                    value={formData.period_start}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Period End *</Label>
                  <Input
                    type="date"
                    name="period_end"
                    value={formData.period_end}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={3}>
                <FormGroup>
                  <Label>Total Revenue *</Label>
                  <InputGroup>
                    <InputGroupText>₹</InputGroupText>
                    <Input
                      type="number"
                      name="total_revenue"
                      value={formData.total_revenue}
                      onChange={handleInputChange}
                      placeholder="0.00"
                      step="0.01"
                      required
                    />
                  </InputGroup>
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label>Commission % *</Label>
                  <InputGroup>
                    <Input
                      type="number"
                      name="commission_percent"
                      value={formData.commission_percent}
                      onChange={handleInputChange}
                      placeholder="0.00"
                      step="0.01"
                      required
                    />
                    <InputGroupText>%</InputGroupText>
                  </InputGroup>
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label>Commission Amount</Label>
                  <InputGroup>
                    <InputGroupText>₹</InputGroupText>
                    <Input
                      type="number"
                      name="commission_amount"
                      value={formData.commission_amount}
                      readOnly
                      className="bg-light"
                    />
                  </InputGroup>
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label>Tax Amount (18%)</Label>
                  <InputGroup>
                    <InputGroupText>₹</InputGroupText>
                    <Input
                      type="number"
                      name="tax_amount"
                      value={formData.tax_amount}
                      readOnly
                      className="bg-light"
                    />
                  </InputGroup>
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label>Amount Due</Label>
                  <InputGroup>
                    <InputGroupText>₹</InputGroupText>
                    <Input
                      type="number"
                      name="amount_due"
                      value={formData.amount_due}
                      readOnly
                      className="bg-light"
                    />
                  </InputGroup>
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>Net Payable</Label>
                  <InputGroup>
                    <InputGroupText>₹</InputGroupText>
                    <Input
                      type="number"
                      name="net_payable"
                      value={formData.net_payable}
                      readOnly
                      className="bg-light"
                    />
                  </InputGroup>
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>Payment Mode</Label>
                  <Input
                    type="select"
                    name="payment_mode"
                    value={formData.payment_mode}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Mode</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="upi">UPI</option>
                    <option value="cheque">Cheque</option>
                    <option value="cash">Cash</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label>Transaction ID</Label>
                  <Input
                    type="text"
                    name="transaction_id"
                    value={formData.transaction_id}
                    onChange={handleInputChange}
                    placeholder="Transaction reference ID"
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>Payout Date</Label>
                  <Input
                    type="date"
                    name="payout_date"
                    value={formData.payout_date}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>Remarks</Label>
                  <Input
                    type="textarea"
                    name="remarks"
                    value={formData.remarks}
                    onChange={handleInputChange}
                    rows={1}
                    placeholder="Additional remarks"
                  />
                </FormGroup>
              </Col>
            </Row>

            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button 
                type="button" 
                color="secondary" 
                onClick={() => navigate("/settlements")}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                color="primary" 
                disabled={loading}
              >
                <FaSave className="me-1" />
                {loading ? "Creating..." : "Create Settlement"}
              </Button>
            </div>
          </Form>
        </CardBody>
      </Card>
    </div>
  );
};

export default CreateSettlement;